import { useNavigate, Link } from "react-router-dom";
import { SYLLAPLUS_LOGO_B64 } from "../constants";

const NAVY = "#1C2B3A";
const CREAM = "#F8F7F5";
const GRAY = "#6B7280";
const LIGHT_BORDER = "#E5E7EB";

export default function LandingPage() {
  const navigate = useNavigate();

  const goToApp = () => navigate("/app");

  return (
    <div style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif", background: "#FFFFFF", color: NAVY, minHeight: "100vh" }}>

      {/* ── Nav ── */}
      <nav style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(255,255,255,0.95)", backdropFilter: "blur(8px)", borderBottom: `1px solid ${LIGHT_BORDER}`, padding: "0 40px", display: "flex", alignItems: "center", justifyContent: "space-between", height: "64px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <img src={`data:image/png;base64,${SYLLAPLUS_LOGO_B64}`} alt="SyllaPlus" style={{ width: "32px", height: "32px" }} />
          <span style={{ fontSize: "18px", fontWeight: "600", color: NAVY, letterSpacing: "-0.3px" }}>SyllaPlus</span>
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <button onClick={goToApp} style={{ padding: "8px 20px", background: "transparent", border: `1.5px solid ${NAVY}`, borderRadius: "8px", fontSize: "14px", fontWeight: "500", color: NAVY, cursor: "pointer" }}>Log in</button>
          <button onClick={goToApp} style={{ padding: "8px 20px", background: NAVY, border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "500", color: "white", cursor: "pointer" }}>Get Started</button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section style={{ padding: "100px 40px 80px", textAlign: "center", maxWidth: "860px", margin: "0 auto" }}>
        <div style={{ display: "inline-block", background: CREAM, border: `1px solid ${LIGHT_BORDER}`, borderRadius: "20px", padding: "6px 16px", fontSize: "13px", color: GRAY, marginBottom: "28px", letterSpacing: "0.02em" }}>
          Built for university faculty
        </div>
        <h1 style={{ fontSize: "clamp(36px, 6vw, 68px)", fontWeight: "800", lineHeight: "1.05", letterSpacing: "-2px", color: NAVY, margin: "0 0 24px 0" }}>
          MAKE YOUR SYLLABUS<br />WORK FOR YOU
        </h1>
        <p style={{ fontSize: "18px", color: GRAY, lineHeight: "1.7", maxWidth: "600px", margin: "0 auto 40px", fontWeight: "400" }}>
          SyllaPlus uses AI to organize your course materials, generate professional syllabi, and keep your calendar on track — so you can focus on teaching.
        </p>
        <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={goToApp} style={{ padding: "14px 32px", background: NAVY, color: "white", border: "none", borderRadius: "10px", fontSize: "16px", fontWeight: "600", cursor: "pointer", letterSpacing: "-0.2px" }}>
            Get Started Free →
          </button>
          <button onClick={() => document.getElementById("features").scrollIntoView({ behavior: "smooth" })} style={{ padding: "14px 32px", background: "transparent", color: NAVY, border: `1.5px solid ${LIGHT_BORDER}`, borderRadius: "10px", fontSize: "16px", fontWeight: "500", cursor: "pointer" }}>
            See How It Works
          </button>
        </div>
      </section>

      {/* ── Divider ── */}
      <div style={{ borderTop: `1px solid ${LIGHT_BORDER}`, maxWidth: "900px", margin: "0 auto" }} />

      {/* ── Features ── */}
      <section id="features" style={{ padding: "80px 40px", maxWidth: "900px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "56px" }}>
          <h2 style={{ fontSize: "36px", fontWeight: "700", color: NAVY, margin: "0 0 12px 0", letterSpacing: "-1px" }}>Everything a professor needs</h2>
          <p style={{ fontSize: "16px", color: GRAY, margin: 0 }}>Three powerful tools, one simple platform.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "24px" }}>
          {[
            {
              icon: "🔍",
              title: "Semantic Search",
              desc: "Find exactly what you need. SyllaPlus understands the meaning behind your search — not just the words — so the right materials surface instantly."
            },
            {
              icon: "✨",
              title: "Syllabus Studio",
              desc: "Generate a complete, professional syllabus from your course materials in seconds. Redesign existing courses with AI guidance and export to Word or PDF."
            },
            {
              icon: "📅",
              title: "Smart Calendar",
              desc: "Build your course calendar automatically. SyllaPlus maps your materials to the right weeks so every lecture is prepared before the semester starts."
            }
          ].map(f => (
            <div key={f.title} style={{ padding: "32px 28px", background: CREAM, borderRadius: "16px", border: `1px solid ${LIGHT_BORDER}` }}>
              <div style={{ fontSize: "28px", marginBottom: "16px" }}>{f.icon}</div>
              <h3 style={{ fontSize: "18px", fontWeight: "600", color: NAVY, margin: "0 0 10px 0" }}>{f.title}</h3>
              <p style={{ fontSize: "14px", color: GRAY, lineHeight: "1.7", margin: 0 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── How It Works ── */}
      <section style={{ padding: "80px 40px", background: CREAM, borderTop: `1px solid ${LIGHT_BORDER}`, borderBottom: `1px solid ${LIGHT_BORDER}` }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "56px" }}>
            <h2 style={{ fontSize: "36px", fontWeight: "700", color: NAVY, margin: "0 0 12px 0", letterSpacing: "-1px" }}>How it works</h2>
            <p style={{ fontSize: "16px", color: GRAY, margin: 0 }}>From upload to syllabus in three steps.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "32px" }}>
            {[
              { step: "01", title: "Upload your materials", desc: "PDFs, Word docs, PowerPoints — upload everything related to your course." },
              { step: "02", title: "Let AI do the work", desc: "SyllaPlus organizes, searches, and analyzes your materials automatically." },
              { step: "03", title: "Export and teach", desc: "Download a professional syllabus and a fully built course calendar." }
            ].map(s => (
              <div key={s.step} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <span style={{ fontSize: "13px", fontWeight: "700", color: NAVY, opacity: 0.3, letterSpacing: "0.1em" }}>{s.step}</span>
                <h3 style={{ fontSize: "17px", fontWeight: "600", color: NAVY, margin: 0 }}>{s.title}</h3>
                <p style={{ fontSize: "14px", color: GRAY, lineHeight: "1.7", margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Our Story ── */}
      <section style={{ padding: "80px 40px", maxWidth: "900px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "48px" }}>
          <h2 style={{ fontSize: "36px", fontWeight: "700", color: NAVY, margin: "0 0 12px 0", letterSpacing: "-1px" }}>Our Story</h2>
        </div>
        <p style={{ fontSize: "16px", color: GRAY, lineHeight: "1.9", maxWidth: "680px", margin: "0 auto 56px", textAlign: "center" }}>
          SyllaPlus was born from a real problem. Aila, an adjunct faculty member teaching across multiple universities, described the exhausting reality of managing course materials scattered across drives, emails, and binders — rebuilding syllabi from scratch every semester with no good tools to help. Vinay, an engineer who saw the problem up close, built SyllaPlus to fix it. What started as a solution for one professor is now a platform built for all of them.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "32px", maxWidth: "500px", margin: "0 auto" }}>
          {[
            { name: "Aila Boyd", title: "Co-Founder & Domain Advisor" },
            { name: "Vinay Jadhav", title: "Co-Founder & Engineer" }
          ].map(f => (
            <div key={f.name} style={{ textAlign: "center" }}>
              <div style={{ width: "80px", height: "80px", borderRadius: "50%", background: NAVY, margin: "0 auto 14px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px", color: "white", fontWeight: "600" }}>
                {f.name.charAt(0)}
              </div>
              <div style={{ fontSize: "16px", fontWeight: "600", color: NAVY, marginBottom: "4px" }}>{f.name}</div>
              <div style={{ fontSize: "13px", color: GRAY }}>{f.title}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section style={{ padding: "80px 40px", background: NAVY, textAlign: "center" }}>
        <h2 style={{ fontSize: "36px", fontWeight: "700", color: "white", margin: "0 0 16px 0", letterSpacing: "-1px" }}>Ready to take back your time?</h2>
        <p style={{ fontSize: "16px", color: "rgba(255,255,255,0.65)", margin: "0 0 36px 0" }}>Join faculty who are already teaching smarter with SyllaPlus.</p>
        <button onClick={goToApp} style={{ padding: "14px 36px", background: "white", color: NAVY, border: "none", borderRadius: "10px", fontSize: "16px", fontWeight: "600", cursor: "pointer" }}>
          Get Started Free →
        </button>
      </section>

      {/* ── Footer ── */}
      <footer style={{ padding: "32px 40px", borderTop: `1px solid ${LIGHT_BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img src={`data:image/png;base64,${SYLLAPLUS_LOGO_B64}`} alt="SyllaPlus" style={{ width: "24px", height: "24px" }} />
          <span style={{ fontSize: "14px", fontWeight: "600", color: NAVY }}>SyllaPlus</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
          <Link to="/privacy" style={{ fontSize: "13px", color: GRAY, textDecoration: "none" }}>Privacy Policy</Link>
          <Link to="/terms" style={{ fontSize: "13px", color: GRAY, textDecoration: "none" }}>Terms of Service</Link>
          <span style={{ fontSize: "13px", color: GRAY }}>© 2026 SyllaPlus. Built for faculty, by faculty.</span>
        </div>
      </footer>

    </div>
  );
}